/**
 * 책상 임팩트 금 데칼. **중량 물건(모니터/맥북 — mass ≥ BALANCE.deskBreak.minMass)** 이 책상
 * 위에서 부서질 때만 부딪힌 자리(XZ)에 목재 쪼개짐 균열 데칼을 상판 바로 위에 눕혀 남긴다.
 * 가벼운 물건(머그/펭귄 등)은 금이 가지 않는다. 세게 부술수록 크게. 링버퍼로 최대 수를
 * 캡하고(오래된 것부터 재사용) 세션 시작/리셋·책상 파괴 때 모두 지운다.
 *
 * 퀄리티: 256px 절차 텍스처 3종(변형) — 끝으로 갈수록 가늘어지는 테이퍼 균열 + 균열 옆
 * 밝은 목질 스플린터 하이라이트(패인 깊이감) + 잔가지/실금 + 중심 눌림 자국.
 */
import * as THREE from 'three';
import type { EventBus } from '../core/EventBus';
import type { Vec3 } from '../core/types';
import { LAYOUT } from '../config/layout';
import { BALANCE } from '../config/balance';
import { getObjectDef } from '../config/objects';
import { makeCanvas, toTexture } from './canvas2d';
import { mulberry32 } from './rng';

const MAX_CRACKS = 28;
const VARIANTS = 3;
/** 상판(+매트) 바로 위 — z-fighting 방지용 미세 오프셋. */
const CRACK_Y = LAYOUT.desk.topY + 0.014;

/** 테이퍼 폴리라인: 시작 굵기 → 끝 0.4px로 가늘어지며 세그먼트별로 그린다. */
function taperedLine(
  ctx: CanvasRenderingContext2D,
  pts: { x: number; y: number }[],
  width0: number,
  color: (t: number) => string,
): void {
  for (let i = 0; i < pts.length - 1; i++) {
    const t = i / (pts.length - 1);
    ctx.strokeStyle = color(t);
    ctx.lineWidth = Math.max(0.4, width0 * (1 - t * 0.85));
    ctx.beginPath();
    ctx.moveTo(pts[i].x, pts[i].y);
    ctx.lineTo(pts[i + 1].x, pts[i + 1].y);
    ctx.stroke();
  }
}

/** 목재 쪼개짐 임팩트 균열 텍스처(투명 배경). seed로 변형 생성. */
function makeCrackTexture(seed: number): THREE.Texture {
  const S = 256;
  const cx = S / 2;
  const cy = S / 2;
  const { canvas, ctx } = makeCanvas(S, S);
  const rnd = mulberry32(seed);
  ctx.lineCap = 'round';

  // (1) 중심 눌림 자국 — 짓눌린 목질 섬유(어두운 방사 그라데이션, 살짝 타원).
  const bruise = ctx.createRadialGradient(cx, cy, 0, cx, cy, 26);
  bruise.addColorStop(0, 'rgba(18,12,8,0.5)');
  bruise.addColorStop(0.55, 'rgba(24,16,10,0.22)');
  bruise.addColorStop(1, 'rgba(24,16,10,0)');
  ctx.fillStyle = bruise;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rnd() * Math.PI);
  ctx.scale(1, 0.75 + rnd() * 0.3);
  ctx.beginPath();
  ctx.arc(0, 0, 26, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // (2) 주 균열 6~9개 — 지그재그 경로를 미리 만들고, 하이라이트(옆) → 본체(위) 순서로 그림.
  const spokes = 6 + Math.floor(rnd() * 4);
  for (let i = 0; i < spokes; i++) {
    const base = (i / spokes) * Math.PI * 2 + rnd() * 0.6;
    const len = 62 + rnd() * 54;
    const segs = 5 + Math.floor(rnd() * 3);
    const pts = [{ x: cx, y: cy }];
    let a = base;
    for (let s = 1; s <= segs; s++) {
      a = base + (rnd() - 0.5) * 0.55;
      const r = (len * s) / segs;
      pts.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
    }
    // 균열 옆 밝은 목질 스플린터(수직 오프셋) — 패여서 속살이 드러난 깊이감.
    const off = 1.6;
    const ox = Math.cos(base + Math.PI / 2) * off;
    const oy = Math.sin(base + Math.PI / 2) * off;
    taperedLine(
      ctx,
      pts.map((p) => ({ x: p.x + ox, y: p.y + oy })),
      2.4,
      (t) => `rgba(201,160,106,${0.5 * (1 - t)})`,
    );
    // 균열 본체 — 깊은 다크 피셔(테이퍼).
    taperedLine(ctx, pts, 3.6 + rnd() * 1.6, (t) => `rgba(10,7,5,${0.95 - t * 0.35})`);

    // 잔가지 1~2개(주 균열 중간에서 갈라져 나감).
    const branches = rnd() < 0.75 ? 1 + Math.floor(rnd() * 2) : 0;
    for (let b = 0; b < branches; b++) {
      const from = pts[1 + Math.floor(rnd() * (pts.length - 2))];
      const ba = base + (rnd() < 0.5 ? 1 : -1) * (0.7 + rnd() * 0.5);
      const bl = 14 + rnd() * 22;
      const bp = [from];
      for (let s = 1; s <= 3; s++) {
        bp.push({
          x: from.x + Math.cos(ba + (rnd() - 0.5) * 0.4) * ((bl * s) / 3),
          y: from.y + Math.sin(ba + (rnd() - 0.5) * 0.4) * ((bl * s) / 3),
        });
      }
      taperedLine(ctx, bp, 1.6, (t) => `rgba(12,8,6,${0.7 - t * 0.3})`);
    }
  }

  // (3) 실금(hairline) — 중심부 주변 미세 균열.
  for (let h = 0; h < 12; h++) {
    const ha = rnd() * Math.PI * 2;
    const hr = 8 + rnd() * 30;
    const hl = 6 + rnd() * 14;
    ctx.strokeStyle = `rgba(14,10,7,${0.25 + rnd() * 0.25})`;
    ctx.lineWidth = 0.6;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(ha) * hr, cy + Math.sin(ha) * hr);
    ctx.lineTo(cx + Math.cos(ha + (rnd() - 0.5) * 0.6) * (hr + hl), cy + Math.sin(ha + (rnd() - 0.5) * 0.6) * (hr + hl));
    ctx.stroke();
  }

  // (4) 동심 부분 아치(충격파 링) 2~3개.
  for (const rr of [16, 30, 46].slice(0, 2 + Math.floor(rnd() * 2))) {
    const s0 = rnd() * Math.PI * 2;
    ctx.strokeStyle = 'rgba(12,9,7,0.4)';
    ctx.lineWidth = 0.9;
    ctx.beginPath();
    ctx.arc(cx, cy, rr, s0, s0 + 0.9 + rnd() * 1.3);
    ctx.stroke();
  }

  // (5) 중심 파편 칩 — 튀어나간 밝은 목질 조각들.
  for (let c = 0; c < 6; c++) {
    const ca = rnd() * Math.PI * 2;
    const cr = 3 + rnd() * 12;
    ctx.fillStyle = `rgba(214,176,122,${0.3 + rnd() * 0.3})`;
    ctx.fillRect(cx + Math.cos(ca) * cr, cy + Math.sin(ca) * cr, 1.2 + rnd() * 2.2, 1 + rnd() * 1.4);
  }

  return toTexture(canvas);
}

export class DeskCracks {
  private group = new THREE.Group();
  private decals: THREE.Mesh[] = [];
  private mats: THREE.MeshBasicMaterial[] = [];
  private next = 0;
  /** 책상 파괴 후엔 새 금 생성 중단(책상이 없으므로) — 세션 시작/리셋에 재활성. */
  private active = true;

  constructor(scene: THREE.Scene, bus: EventBus) {
    scene.add(this.group);
    for (let v = 0; v < VARIANTS; v++) {
      this.mats.push(
        new THREE.MeshBasicMaterial({
          map: makeCrackTexture(0x5eed + v * 7919),
          transparent: true,
          depthWrite: false, // 반투명 데칼 — 위 물건/파편에 가려지되 상판과 z-fight 안 함
          opacity: 0.9,
        }),
      );
    }
    const geo = new THREE.PlaneGeometry(1, 1);
    for (let i = 0; i < MAX_CRACKS; i++) {
      const m = new THREE.Mesh(geo, this.mats[0]);
      m.rotation.x = -Math.PI / 2; // 상판에 눕힘(위를 향함)
      m.renderOrder = 2;
      m.visible = false;
      this.group.add(m);
      this.decals.push(m);
    }

    bus.on('object:broken', (e) => this.onBroken(e.defId, e.position, e.impactSpeed));
    bus.on('desk:destroyed', () => {
      this.clear();
      this.active = false; // 책상이 사라졌으니 이후 파괴(전체 일괄 파괴)에 금 생성 금지
    });
    const reset = (): void => {
      this.clear();
      this.active = true;
    };
    bus.on('session:start', reset);
    bus.on('session:reset', reset);
  }

  /** 현재 표시 중인 금 데칼 수(검증용). */
  get count(): number {
    let n = 0;
    for (const m of this.decals) if (m.visible) n++;
    return n;
  }

  /** 중량 물건이 책상 위(상판 근처)에서 부서졌을 때만 금 데칼을 남긴다. */
  private onBroken(defId: string, pos: Vec3, speed: number): void {
    if (!this.active) return;
    if (getObjectDef(defId).mass < BALANCE.deskBreak.minMass) return; // 가벼운 물건은 금 없음
    const halfW = LAYOUT.desk.size[0] / 2;
    const halfD = LAYOUT.desk.size[2] / 2;
    if (Math.abs(pos.x) > halfW || Math.abs(pos.z) > halfD) return;
    // 상판 근처에서 부서진 것만 — 위(공중/천장)도, 아래(책상 밑 공간)도 제외.
    if (pos.y > LAYOUT.desk.topY + 0.6 || pos.y < LAYOUT.desk.topY - 0.25) return;

    const m = this.decals[this.next];
    this.next = (this.next + 1) % MAX_CRACKS;
    m.material = this.mats[Math.floor(Math.random() * VARIANTS)];
    const size = 0.5 + Math.min(1, speed / 20) * 0.65; // 세게 부술수록 큰 금
    m.position.set(pos.x, CRACK_Y, pos.z);
    m.scale.set(size, size, 1);
    m.rotation.z = Math.random() * Math.PI * 2; // 눕힌 평면에서 무작위 회전(반복감 감소)
    m.visible = true;
  }

  /** 모든 금 데칼 숨김(세션 시작/리셋·책상 파괴). */
  private clear(): void {
    for (const m of this.decals) m.visible = false;
    this.next = 0;
  }
}
